import { useMutation, useQueryClient } from '@tanstack/react-query';
import { INPUTS_QUERY_KEY } from 'constants/queryKeys';
import { IInput } from 'interfaces/IInput';
import InputService from 'services/InputService';

interface UseCreateInputArgs {
  activeOrganisationId: string | null;
}

/**
 * Creates a new Input document and writes it straight into the org's inputs
 * cache, so the tree picks it up without a refetch. Selection is left to the
 * caller via a per-call onSuccess.
 */
export const useCreateInput = ({
  activeOrganisationId,
}: UseCreateInputArgs) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: IInput) => {
      const clientNow = new Date().toISOString();
      return InputService.CreateDoc(payload, clientNow);
    },
    onSuccess: (response, payload) => {
      if (!activeOrganisationId) return;

      // Create returns the same { data, changes } envelope as UpdateDoc. Fall
      // back to the sent payload so the node still appears if data is empty.
      const createdInput = response?.data?.[0] ?? payload;

      queryClient.setQueryData<IInput[]>(
        [INPUTS_QUERY_KEY, activeOrganisationId],
        (current) => {
          if (!current) return current;
          if (current.some((item) => item.id === createdInput.id)) {
            return current;
          }
          return [...current, createdInput];
        }
      );
    },
  });
};

export default useCreateInput;
